/**
 * useSwipeTabs - Hook for swipe gesture navigation between tabs
 *
 * Provides touch gesture support for tab navigation on mobile devices.
 * Implements swipe left/right to navigate between dimension tabs.
 *
 * @see Story 5.2: Swipe Gesture Tabs
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useDrag, type FullGestureState } from '@use-gesture/react';

/** Options for the useSwipeTabs hook */
export interface UseSwipeTabsOptions {
  /** Array of tab identifiers */
  tabs: string[];
  /** Currently active tab identifier */
  activeTab: string;
  /** Callback when tab changes via swipe */
  onTabChange: (tab: string) => void;
  /** Minimum swipe distance in pixels to trigger navigation (default: 50) */
  threshold?: number;
  /** Minimum velocity to trigger navigation with smaller distance */
  velocityThreshold?: number;
  /** Minimum distance when velocity threshold is met */
  velocityMinDistance?: number;
  /** Edge margin to avoid browser back gesture conflict (default: 20) */
  edgeMargin?: number;
}

/** Return type for the useSwipeTabs hook */
export interface UseSwipeTabsReturn {
  /** Props to spread on the swipeable container */
  bind: ReturnType<typeof useDrag>;
  /** Current swipe progress (-1 to 1, where negative = swiping right, positive = swiping left) */
  swipeProgress: number;
  /** Whether a swipe is currently in progress */
  isSwiping: boolean;
  /** Direction of current swipe ('left' | 'right' | null) */
  swipeDirection: 'left' | 'right' | null;
  /** Whether reduced motion is preferred */
  prefersReducedMotion: boolean;
}

/**
 * Check if an element or its ancestors have horizontal scrolling enabled
 */
function hasHorizontalScroll(element: HTMLElement | null): boolean {
  let current = element;
  while (current) {
    // Check for data-scrollable attribute (for explicit chart containers)
    if (current.dataset.scrollable === 'true' || current.dataset.scrollable === 'horizontal') {
      return true;
    }

    // Check computed style for overflow-x
    const style = window.getComputedStyle(current);
    const overflowX = style.overflowX;
    if (overflowX === 'auto' || overflowX === 'scroll') {
      // Only consider it scrollable if content actually overflows
      if (current.scrollWidth > current.clientWidth) {
        return true;
      }
    }

    current = current.parentElement;
  }
  return false;
}

/**
 * Check if gesture started too close to screen edge (browser back gesture zone)
 */
function isNearScreenEdge(clientX: number, edgeMargin: number): boolean {
  return clientX < edgeMargin || clientX > window.innerWidth - edgeMargin;
}

/**
 * Hook for swipe gesture navigation between tabs
 *
 * @example
 * ```tsx
 * const { bind, swipeProgress, isSwiping } = useSwipeTabs({
 *   tabs: ['revenue', 'costs', 'margin'],
 *   activeTab: 'revenue',
 *   onTabChange: setActiveTab,
 * });
 *
 * return (
 *   <div {...bind()}>
 *     <TabContent style={{ transform: `translateX(${swipeProgress * -50}px)` }} />
 *   </div>
 * );
 * ```
 */
export function useSwipeTabs({
  tabs,
  activeTab,
  onTabChange,
  threshold = 50,
  velocityThreshold = 0.5,
  velocityMinDistance = 20,
  edgeMargin = 20,
}: UseSwipeTabsOptions): UseSwipeTabsReturn {
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Track if we should ignore this gesture (started on scrollable element or near edge)
  const ignoreGestureRef = useRef(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent): void => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Get current tab index
  const currentIndex = tabs.indexOf(activeTab);

  // Navigate to adjacent tab
  const navigateTab = useCallback(
    (direction: 'prev' | 'next') => {
      if (direction === 'prev' && currentIndex > 0) {
        onTabChange(tabs[currentIndex - 1]);
      } else if (direction === 'next' && currentIndex < tabs.length - 1) {
        onTabChange(tabs[currentIndex + 1]);
      }
    },
    [currentIndex, tabs, onTabChange]
  );

  // Handle drag gesture
  const bind = useDrag(
    (state: FullGestureState<'drag'>) => {
      const {
        movement: [mx],
        velocity: [vx],
        direction: [dx],
        first,
        last,
        event,
        cancel,
      } = state;

      // On first touch, check if we should ignore this gesture
      if (first) {
        const target = event?.target as HTMLElement | null;
        const touchEvent = event as TouchEvent | undefined;
        const clientX = touchEvent?.touches?.[0]?.clientX ?? (event as MouseEvent)?.clientX ?? 0;

        // Ignore if started on scrollable element
        if (hasHorizontalScroll(target)) {
          ignoreGestureRef.current = true;
          cancel?.();
          return;
        }

        // Ignore if started near screen edge (browser back gesture)
        if (isNearScreenEdge(clientX, edgeMargin)) {
          ignoreGestureRef.current = true;
          cancel?.();
          return;
        }

        ignoreGestureRef.current = false;
        setIsSwiping(true);
      }

      // Skip if gesture was marked to ignore
      if (ignoreGestureRef.current) {
        return;
      }

      // Calculate swipe progress (normalized -1 to 1)
      // Negative mx = swiping left (going to next tab)
      // Positive mx = swiping right (going to previous tab)
      const maxSwipe = threshold * 2;
      const normalizedProgress = Math.max(-1, Math.min(1, mx / maxSwipe));

      // Update direction
      if (mx < -10) {
        setSwipeDirection('left');
      } else if (mx > 10) {
        setSwipeDirection('right');
      } else {
        setSwipeDirection(null);
      }

      // Update progress (skip animation updates if reduced motion is preferred)
      if (!prefersReducedMotion) {
        setSwipeProgress(normalizedProgress);
      }

      // On gesture end, determine if we should navigate
      if (last) {
        setIsSwiping(false);
        setSwipeProgress(0);
        setSwipeDirection(null);

        const absMovement = Math.abs(mx);
        const absVelocity = Math.abs(vx);

        // Check if swipe meets threshold criteria
        // Either: movement > threshold, OR (velocity > velocityThreshold AND movement > velocityMinDistance)
        const meetsThreshold =
          absMovement > threshold || (absVelocity > velocityThreshold && absMovement > velocityMinDistance);

        if (meetsThreshold) {
          // dx > 0 means swiping right (go to previous)
          // dx < 0 means swiping left (go to next)
          if (dx < 0) {
            navigateTab('next');
          } else if (dx > 0) {
            navigateTab('prev');
          }
        }
      }
    },
    {
      axis: 'x',
      filterTaps: true,
      threshold: 10,
      pointer: { touch: true },
    }
  );

  return {
    bind,
    swipeProgress,
    isSwiping,
    swipeDirection,
    prefersReducedMotion,
  };
}

export default useSwipeTabs;
